import type { PresenceReasonCode, PresenceState } from '@sentra/contracts';
import { OPERATOR_SELECTABLE_REASON_CODES, REASON_CODE_REQUIRING_NOTE } from '@sentra/contracts';
import { TriangleAlert, Volume2, VolumeX } from 'lucide-react';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import { Checkbox } from '@/design/components/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/design/components/dialog';
import { Label } from '@/design/components/label';
import { NativeSelect } from '@/design/components/native-select';
import { Textarea } from '@/design/components/textarea';

/**
 * AD-31's loud anomaly: a full-screen alert, a sound, a reason, and an explicit acknowledgment.
 *
 * **It is deliberately hard to get past.** An anomaly is the moment the system is being told its
 * own record is wrong — the same pass leaving twice, or a blocked person at the door — and the
 * failure it exists to prevent is an operator clearing it by reflex with a queue behind them. So it
 * fills the screen, it makes a noise, it cannot be dismissed by clicking outside or pressing
 * Escape, a reason has to be chosen, `other` demands an explanation, and a separate acknowledgment
 * has to be ticked before the confirmation is live. The way out without recording anything is a
 * button that says so.
 *
 * **The sound is started by the operator's own gesture, which is why it is not started here.**
 * Every browser refuses audio from a page nobody has interacted with, and a scan IS an
 * interaction — a burst of keystrokes, a click on the camera, a press of Check. {@link createAlarm}
 * is therefore primed on that gesture, by the screen that handled it, and this dialog only asks it
 * to play. An alarm constructed at this moment would be silent on exactly the terminals where it
 * matters most, and nothing would say so.
 *
 * **`system_correction` is not on the picker**, because it is the reason a bulk correction gives.
 * The list comes from the contracts package rather than from a literal here, so the picker and the
 * server's own refusal cannot disagree about who may choose what.
 *
 * **Nothing is submitted until every requirement is met.** The confirm button is disabled, and the
 * dialog's own state is what the caller receives: there is no path that sends a movement with no
 * reason, which is the negative assertion the tests make.
 *
 * **The silence control is a toggle that says which way it is, and it is separated from the two
 * decisions.** It used to be a `ghost` button with no border, no icon and no way back: pressing it
 * stopped the sound and changed nothing on screen, so on a gate with an alarm going a control that
 * worked and one that was broken looked identical. It now carries a mute icon, an outline, and a
 * label that names the next act — and it sits at the far end of the row, because silencing an
 * alarm is not one of the two things this dialog is asking. **There is deliberately no
 * `aria-pressed`**: a control that both renames itself to the next action and reports a pressed
 * state announces "Restore the sound, pressed", which contradicts itself. The changed accessible
 * name is the state; `data-muted` carries it for a test.
 */

/** What the operator has to decide before an anomaly can be recorded. */
export interface AnomalyDecision {
  readonly reasonCode: PresenceReasonCode;
  /** Required for `other`, and `null` for every other reason. */
  readonly reasonNote: string | null;
}

/**
 * Which anomaly this is, which is the only thing that differs between the two.
 *
 * **Both demand exactly the same of the operator** — a reason from the closed list and an explicit
 * acknowledgment — so this decides the sentence at the top and nothing else. It was two independent
 * booleans while a blocked person additionally needed somebody else's credentials; with that gone
 * there is one axis left, and a second one would be a field with a single value.
 *
 * `blocked` covers a blocked person walking EITHER way, because the matrix does: their state is not
 * what is wrong, their record is.
 */
export type AnomalyKind = 'contradiction' | 'blocked';

/**
 * The alert sound.
 *
 * Synthesised rather than loaded from a file: an audio asset would be bytes in the bundle for two
 * seconds of sound, and a gate terminal that failed to fetch it would fail silently, which for an
 * alarm is the one failure mode that must not happen quietly.
 */
export interface Alarm {
  /**
   * Called from inside the operator's own gesture, before there is anything to announce.
   *
   * This is the whole of the autoplay story: a browser will not start audio for a page nobody has
   * touched, and by the time the anomaly is known the gesture that produced it is over.
   */
  readonly prime: () => void;
  readonly play: () => void;
  readonly stop: () => void;
}

/** The slice of the Web Audio API this needs, so a test can hand in a double. */
export interface AudioPlatform {
  readonly state: string;
  resume: () => Promise<void>;
  createOscillator: () => {
    type: string;
    frequency: { value: number };
    connect: (destination: unknown) => void;
    start: () => void;
    stop: () => void;
  };
  createGain: () => { gain: { value: number }; connect: (destination: unknown) => void };
  readonly destination: unknown;
}

/** The pitch and loudness of the alert. Loud enough to be heard across a gate, and not painful. */
const ALARM_HERTZ = 880;
const ALARM_GAIN = 0.2;

export function createAlarm(open: () => AudioPlatform | null): Alarm {
  let context: AudioPlatform | null = null;
  let playing: { stop: () => void } | null = null;

  return {
    prime: () => {
      context ??= open();
      if (context !== null && context.state === 'suspended') {
        void context.resume();
      }
    },
    play: () => {
      if (context === null || playing !== null) {
        return;
      }
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'square';
      oscillator.frequency.value = ALARM_HERTZ;
      gain.gain.value = ALARM_GAIN;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      playing = oscillator;
    },
    stop: () => {
      playing?.stop();
      playing = null;
    },
  };
}

/**
 * The browser's own audio, or `null` where there is none.
 *
 * Narrowed through `unknown` rather than typed against the DOM's `AudioContext`, because what this
 * module needs is four members and the platform's own type is a hundred: declaring the slice is
 * what lets a test hand in a double that records what was played, which for an alarm is the only
 * way to prove it sounded at all.
 */
export function browserAudio(): AudioPlatform | null {
  const platform = globalThis as unknown as { AudioContext?: new () => AudioPlatform };
  const Context = platform.AudioContext;
  return Context === undefined ? null : new Context();
}

/**
 * **Every decision is per anomaly, which is why the caller MOUNTS this rather than toggling it.**
 * Carrying a reason or a note over to the next person would attach one operator's explanation to
 * somebody else's movement, permanently, on an append-only row — and a mute carried over would
 * silence every later alert on a shared terminal. Rendering the dialog only while there is an
 * anomaly makes every one of those fresh by construction, with no effect to remember to write and
 * none to forget to update when a field is added.
 */
export function AnomalyDialog({
  open,
  kind,
  presenceState,
  alarm,
  submitting,
  failure,
  onConfirm,
  onCancel,
}: Readonly<{
  open: boolean;
  kind: AnomalyKind;
  /** Where the person is recorded as being, which is what makes the contradiction legible. */
  presenceState: PresenceState;
  alarm: Alarm;
  submitting: boolean;
  /**
   * Why the last attempt from inside this dialog was refused, or `null`.
   *
   * It is rendered HERE rather than behind the dialog, which is the only place an operator can read
   * it: this is a modal with an overlay, so a banner on the screen underneath is unreachable.
   * Without it, pressing the button on the most safety-relevant interaction in the product appears
   * to do nothing at all, twice.
   */
  readonly failure: string | null;
  onConfirm: (decision: AnomalyDecision) => void;
  onCancel: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const reasonId = useId();
  const noteId = useId();

  const [reasonCode, setReasonCode] = useState<PresenceReasonCode | ''>('');
  const [note, setNote] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [muted, setMuted] = useState(false);

  // The sound follows the dialog, and stops when it closes however it closed. A dialog that
  // recorded the movement and left the alarm ringing would be worse than one that never sounded.
  useEffect(() => {
    if (open && !muted) {
      alarm.play();
      return () => {
        alarm.stop();
      };
    }
    alarm.stop();
    return undefined;
  }, [alarm, muted, open]);

  const noteRequired = reasonCode === REASON_CODE_REQUIRING_NOTE;
  const noteMissing = noteRequired && note.trim().length === 0;
  const ready = reasonCode !== '' && !noteMissing && acknowledged;

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        closeLabel={t('common.close')}
        // Neither of these dismisses it. An anomaly cleared by a stray click outside is an anomaly
        // nobody decided about, and the row would still say an operator did.
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        data-anomaly={kind}
        // Section 1.11 says a FULL-SCREEN red alert, and it means it: this has to be impossible to
        // work around or look past with somebody standing in front of the operator.
        //
        // **`bg-alert-surface` is OPAQUE, and that is the whole of it.** A ten-percent red wash
        // reads as one over a card and does not over the dialog's own overlay, which is
        // `bg-black/50`: this content covers the viewport, so the wash composited to a dark
        // translucent surface and every dark label inside it fell below the contrast floor. The
        // token is the colour that wash was meant to be, made solid and measured.
        className="inset-0 top-0 left-0 h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 overflow-y-auto rounded-none border-0 bg-alert-surface p-6 sm:max-w-none"
      >
        <div className="grid gap-4">
          <div className="flex items-center gap-3 rounded-lg bg-danger p-4 text-danger-foreground">
            <TriangleAlert aria-hidden className="size-8 shrink-0" />
            <DialogTitle className="text-2xl font-bold">{t('scan.anomaly.title')}</DialogTitle>
          </div>

          <DialogDescription className="text-base text-foreground">
            {kind === 'blocked'
              ? t('scan.anomaly.blocked')
              : t('scan.anomaly.contradiction', { state: t(`scan.state.${presenceState}`) })}
          </DialogDescription>
          <p className="text-sm text-muted-foreground">{t('scan.anomaly.instruction')}</p>

          <div className="grid gap-1">
            <Label htmlFor={reasonId}>{t('scan.anomaly.reasonLabel')}</Label>
            <NativeSelect
              id={reasonId}
              data-testid="anomaly-reason"
              value={reasonCode}
              aria-describedby={reasonCode === '' ? `${reasonId}-hint` : undefined}
              onChange={(event) => {
                setReasonCode(event.target.value as PresenceReasonCode | '');
              }}
            >
              <option value="">{t('scan.anomaly.reasonPlaceholder')}</option>
              {OPERATOR_SELECTABLE_REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(`scan.reason.${code}`)}
                </option>
              ))}
            </NativeSelect>
            {reasonCode === '' && (
              <p id={`${reasonId}-hint`} className="text-sm text-muted-foreground">
                {t('scan.anomaly.reasonRequired')}
              </p>
            )}
          </div>

          {noteRequired && (
            <div className="grid gap-1">
              <Label htmlFor={noteId}>{t('scan.anomaly.noteLabel')}</Label>
              <Textarea
                id={noteId}
                data-testid="anomaly-note"
                value={note}
                aria-invalid={noteMissing}
                aria-describedby={noteMissing ? `${noteId}-hint` : undefined}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
              />
              {noteMissing && (
                <p id={`${noteId}-hint`} className="text-sm text-danger">
                  {t('scan.anomaly.noteRequired')}
                </p>
              )}
            </div>
          )}

          <label className="flex items-start gap-2 text-sm font-medium">
            <Checkbox
              data-testid="anomaly-acknowledge"
              checked={acknowledged}
              onChange={(event) => {
                setAcknowledged(event.target.checked);
              }}
              className="mt-0.5"
            />
            {t('scan.anomaly.acknowledge')}
          </label>

          {failure !== null && (
            <p role="alert" className="rounded-md border border-danger p-3 text-sm text-danger">
              {failure}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="destructive"
              data-testid="anomaly-confirm"
              disabled={!ready || submitting}
              onClick={() => {
                if (reasonCode === '') {
                  return;
                }
                onConfirm({ reasonCode, reasonNote: noteRequired ? note.trim() : null });
              }}
            >
              {t('scan.anomaly.confirm')}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="anomaly-cancel"
              onClick={onCancel}
              disabled={submitting}
            >
              {t('scan.anomaly.cancel')}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="anomaly-mute"
              data-muted={muted}
              className="ml-auto"
              onClick={() => {
                setMuted((on) => !on);
              }}
            >
              {muted ? <Volume2 aria-hidden /> : <VolumeX aria-hidden />}
              {t(muted ? 'scan.anomaly.unmute' : 'scan.anomaly.mute')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
